import { useState } from 'react';
import { Plus, X } from 'lucide-react';
import clsx from 'clsx';

export interface TabSpec {
  id: string;
  name: string;
  /** Optional: tinted dot to show that filter is active. */
  active?: boolean;
}

interface Props {
  tabs: TabSpec[];
  activeId: string;
  onSelect: (id: string) => void;
  onClose: (id: string) => void;
  onAdd: () => void;
  onRename: (id: string, name: string) => void;
}

/**
 * Browser-style tab strip used to host one logcat session per tab.
 * Renaming is triggered by double-click and confirmed on blur / Enter.
 */
export function TabStrip({ tabs, activeId, onSelect, onClose, onAdd, onRename }: Props) {
  const [editing, setEditing] = useState<string | undefined>();
  return (
    // No horizontal scroll. When tabs would overflow, wrap to a second
    // row. Saves the unsightly thin scroll-strip that always says "more
    // tabs here" and lets every tab stay clickable in one tap.
    <div className="flex flex-wrap items-end gap-1 border-b border-line">
      {tabs.map((t) => (
        <div
          key={t.id}
          className={clsx(
            'group relative -mb-px flex shrink-0 items-center gap-1 rounded-t-md border border-b-0 px-3 py-1 text-xs transition',
            t.id === activeId
              ? 'border-line bg-bg-card text-ink'
              : 'border-transparent text-ink-muted hover:bg-bg-soft'
          )}
          onClick={() => onSelect(t.id)}
        >
          <button
            className="flex items-center gap-1.5"
            onDoubleClick={(e) => {
              e.stopPropagation();
              setEditing(t.id);
            }}
            title="双击重命名"
          >
            {t.active && <span className="inline-block h-1.5 w-1.5 rounded-full bg-emerald-500" />}
            {editing === t.id ? (
              <input
                autoFocus
                className="input h-6 w-32 px-1 py-0 text-xs"
                defaultValue={t.name}
                onClick={(e) => e.stopPropagation()}
                onBlur={(e) => {
                  setEditing(undefined);
                  const v = e.currentTarget.value.trim();
                  if (v && v !== t.name) onRename(t.id, v);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                  if (e.key === 'Escape') setEditing(undefined);
                }}
              />
            ) : (
              <span className="font-medium">{t.name}</span>
            )}
          </button>
          {tabs.length > 1 && (
            <button
              className="rounded p-0.5 text-ink-dim opacity-0 transition group-hover:opacity-100 hover:bg-bg-soft hover:text-rose-500"
              onClick={(e) => {
                e.stopPropagation();
                onClose(t.id);
              }}
              title="关闭标签"
            >
              <X className="h-3 w-3" />
            </button>
          )}
        </div>
      ))}
      <button
        className="ml-1 inline-flex items-center justify-center rounded p-1 text-ink-muted hover:bg-bg-soft hover:text-ink"
        onClick={onAdd}
        title="新建标签"
      >
        <Plus className="h-4 w-4" />
      </button>
    </div>
  );
}
